import { synonymGroups, stopWords } from '../data/guide/synonyms'
import { guideEntries, guideFaqs, getEntry, getCategory, entryPath, faqPath, entryKindLabel, FAQ_CATEGORY_ID } from './guideIndex'
import { levenshtein, normalize } from './text'
import { buildProgressionDocs } from './progressionSearch'
import { detectAction } from './searchIntents'
import type { Block, EntityRef } from '../types/guide'

const WINGS_ONLY = new Set(['ala', 'alas', 'wing', 'wings'])

/*
 * Búsqueda local sobre el contenido publicado (guía + datos del servidor).
 * No genera respuestas: solo encuentra fichas, apartados, mapas, monstruos, spots e ítems existentes.
 */

export type SearchGroup =
  | 'Guías'
  | 'Misiones'
  | 'Recetas'
  | 'Ítems'
  | 'Monstruos'
  | 'Mapas'
  | 'Spots'
  | 'Invasiones'
  | 'Eventos'
  | 'NPC'
  | 'Preguntas frecuentes'

export type Field = { text: string; norm: string; words: string[]; weight: number; label: string }

export type SearchDoc = {
  key: string
  groupKey: string
  group: SearchGroup
  title: string
  sectionTitle?: string
  typeLabel: string
  context?: string
  path: string
  /** Entidad del resultado (tipo + ID exacto): de aquí sale su imagen, igual que en su ficha. */
  entity?: EntityRef
  fields: Field[]
}

export type SearchResult = { doc: SearchDoc; score: number; terms: string[]; reasonLabel: string; snippet: string }
export type SearchResultGroup = { group: SearchGroup; results: SearchResult[] }

const splitWords = (s: string) => s.split(/[^a-z0-9]+/).filter(Boolean)

export function field(text: string, weight: number, label: string): Field {
  const norm = normalize(text)
  return { text, norm, words: splitWords(norm), weight, label }
}

function blockText(b: Block): string {
  switch (b.type) {
    case 'text':
      return b.paragraphs.join(' ')
    case 'facts':
      return b.items.map((f) => `${f.label}: ${f.value}`).join('. ')
    case 'list':
    case 'steps':
      return b.items.join('. ')
    case 'callout':
      return b.text
    case 'worldMaps':
      return b.names.join(', ')
    case 'rewards':
      return b.groups
        .map((g) => `${g.who}. ${g.note ?? ''} ${g.lines.map((l) => `${l.chance ? `${l.chance}%: ` : ''}${l.label}${l.guaranteed ? ' (garantizado)' : ''}`).join('. ')}`)
        .join(' ')
    case 'links':
      return b.items.map((l) => [getEntry(l.entryId)?.title, l.who, l.chance ? `${l.chance}%` : '', l.note].filter(Boolean).join(' ')).join('. ')
  }
}

function groupForEntry(kind: string, categoryId: string): SearchGroup {
  if (kind === 'item') return 'Ítems'
  if (categoryId === 'invasiones-bosses') return 'Invasiones'
  if (categoryId === 'eventos') return 'Eventos'
  return 'Guías'
}

function buildStaticIndex(): SearchDoc[] {
  const docs: SearchDoc[] = []
  guideEntries.forEach((e) => {
    const typeLabel = entryKindLabel(e)
    const group = groupForEntry(e.kind, e.categoryId)
    const context = getCategory(e.categoryId)?.title
    docs.push({
      key: e.id,
      groupKey: e.id,
      group,
      title: e.title,
      typeLabel,
      context,
      path: entryPath(e),
      entity: e.entity,
      fields: [
        field(e.title, 10, 'Título'),
        ...(e.aliases ?? []).map((a) => field(a, 9, 'Nombre alternativo')),
        ...(e.tags ?? []).map((t) => field(t, 5, 'Etiqueta')),
        field(e.summary, 4, 'Descripción'),
      ],
    })
    const sections = e.searchSections ?? e.sections.map((s) => ({ id: s.id, title: s.title, text: s.blocks.map(blockText).join(' ') }))
    sections.forEach((s) => {
      docs.push({
        key: `${e.id}#${s.id}`,
        groupKey: e.id,
        group,
        title: e.title,
        sectionTitle: s.title,
        typeLabel,
        context,
        path: entryPath(e, s.id),
        entity: e.entity,
        fields: [field(s.title, 5, 'Apartado'), field(s.text, 3, s.title), field(e.title, 2, 'Título')],
      })
    })
  })
  guideFaqs.forEach((f) => {
    docs.push({
      key: f.id,
      groupKey: f.id,
      group: 'Preguntas frecuentes',
      title: f.question,
      typeLabel: 'Pregunta frecuente',
      context: getCategory(FAQ_CATEGORY_ID)?.title,
      path: faqPath(f),
      fields: [field(f.question, 8, 'Pregunta frecuente'), ...(f.aliases ?? []).map((a) => field(a, 6, 'Pregunta frecuente')), field(f.answer, 3, 'Respuesta')],
    })
  })
  return docs
}

const STATIC_INDEX = buildStaticIndex()
// Evoluciones, misiones, recetas y NPC: se construyen una vez, en la primera búsqueda.
let PROGRESSION_INDEX: SearchDoc[] | null = null
const STOP = new Set(stopWords)
const SYNONYMS = new Map<string, string[]>()
synonymGroups.forEach((group) => {
  const g = group.map(normalize)
  g.forEach((w) => SYNONYMS.set(w, Array.from(new Set([...(SYNONYMS.get(w) ?? []), ...g.filter((x) => x !== w)]))))
})

function makeSnippet(text: string, terms: string[]): string {
  const norm = normalize(text)
  const positions = terms.filter((t) => t.length >= 2).map((t) => norm.indexOf(t)).filter((i) => i >= 0)
  const idx = positions.length ? Math.min(...positions) : 0
  let start = Math.max(0, idx - 50)
  if (start > 0) {
    const space = text.indexOf(' ', start)
    start = space > -1 && space < idx ? space + 1 : start
  }
  let end = Math.min(text.length, start + 170)
  if (end < text.length) {
    const space = text.lastIndexOf(' ', end)
    end = space > idx ? space : end
  }
  return `${start > 0 ? '…' : ''}${text.slice(start, end).trim()}${end < text.length ? '…' : ''}`
}

function scoreDoc(doc: SearchDoc, q: string, tokens: string[], groups: string[][], allVariants: Set<string>): SearchResult | null {
  let score = 0
  const matched = new Set<string>()
  const covered = new Set<number>()
  let best: { contrib: number; field: Field } = { contrib: 0, field: doc.fields[0] }

  doc.fields.forEach((f) => {
    if (!f.norm) return
    let contrib = 0
    if (f.norm.trim() === q) {
      contrib += 50 * f.weight
      matched.add(q)
    } else if (q.length >= 3 && f.norm.includes(q)) {
      contrib += 20 * f.weight
      matched.add(q)
    }
    groups.forEach((variants, gi) => {
      let g = 0
      variants.forEach((v, vi) => {
        const factor = vi === 0 ? 1 : 0.7
        if (f.words.includes(v)) {
          g = Math.max(g, 6 * factor)
          matched.add(v)
          return
        }
        if (v.length >= 3) {
          const w = f.words.find((x) => x.startsWith(v))
          if (w) {
            g = Math.max(g, 4 * factor)
            matched.add(w)
            return
          }
        }
        if (vi === 0 && v.length >= 4) {
          const tol = v.length >= 7 ? 2 : 1
          const w = f.words.find((x) => x.length >= 3 && levenshtein(x, v, tol) <= tol)
          if (w) {
            g = Math.max(g, 2)
            matched.add(w)
          }
        }
      })
      if (g > 0) {
        covered.add(gi)
        contrib += g * f.weight
      }
    })
    // Bonus si la búsqueda cubre el campo completo (p. ej. "Bull Fighter" frente a "Elite Bull Fighter").
    if (contrib > 0 && f.words.length > 0 && f.words.length <= 6 && f.words.every((w) => allVariants.has(w))) contrib += 15 * f.weight
    if (contrib > best.contrib) best = { contrib, field: f }
    score += contrib
  })

  if (!covered.size) return null
  const coverage = covered.size / tokens.length
  score *= coverage * coverage
  const terms = Array.from(matched)
  return { doc, score, terms, reasonLabel: best.field.label, snippet: makeSnippet(best.field.text, terms) }
}

const GROUP_LIMIT = 4

export function searchGuide(query: string, extraDocs: SearchDoc[] = []): SearchResultGroup[] {
  const q = normalize(query).replace(/[¿?¡!.,;:()"]/g, ' ').replace(/\s+/g, ' ').trim()
  if (q.length < 2) return []

  let tokens = Array.from(new Set(splitWords(q)))
  const meaningful = tokens.filter((t) => !STOP.has(t))
  if (meaningful.length) tokens = meaningful
  if (!tokens.length) return []
  const groups = tokens.map((t) => [t, ...(SYNONYMS.get(t) ?? [])])
  const allVariants = new Set(groups.flat())

  const scored: SearchResult[] = []
  const scan = (docs: SearchDoc[]) =>
    docs.forEach((d) => {
      const r = scoreDoc(d, q, tokens, groups, allVariants)
      if (r) scored.push(r)
    })
  if (!PROGRESSION_INDEX) PROGRESSION_INDEX = buildProgressionDocs()
  scan(STATIC_INDEX)
  // Los documentos de datos sustituyen a los de progresión con la misma clave (p. ej. NPC con ubicación resuelta).
  const extraKeys = new Set(extraDocs.map((d) => d.key))
  scan(extraKeys.size ? PROGRESSION_INDEX.filter((d) => !extraKeys.has(d.key)) : PROGRESSION_INDEX)
  scan(extraDocs)
  if (!scored.length) return []

  // Prioridades por intención: "alas" → guía general de alas; "cómo entro a X" → accesos y requisitos.
  const wingsOnly = tokens.length === 1 && WINGS_ONLY.has(tokens[0])
  const access = detectAction(query) === 'acceso'
  scored.forEach((r) => {
    const base = r.doc.path.split('#')[0]
    if (wingsOnly && base === '/guia/alas') r.score *= 4
    if (access && r.doc.path.includes('#accesos')) r.score *= 3
    // La misión que usa el mapa sigue como resultado relacionado, por debajo del acceso.
    if (access && base === '/guia/evoluciones') r.score *= 0.5
  })

  scored.sort((a, b) => b.score - a.score)
  const floor = scored[0].score * 0.08
  const byGroup = new Map<SearchGroup, SearchResult[]>()
  const perKey = new Map<string, number>()
  for (const r of scored) {
    if (r.score < floor) break
    const list = byGroup.get(r.doc.group) ?? []
    if (list.length >= GROUP_LIMIT) continue
    const n = perKey.get(r.doc.groupKey) ?? 0
    if (n >= 2) continue
    perKey.set(r.doc.groupKey, n + 1)
    list.push(r)
    byGroup.set(r.doc.group, list)
  }
  return Array.from(byGroup.entries())
    .map(([group, results]) => ({ group, results }))
    .sort((a, b) => b.results[0].score - a.results[0].score)
}
